import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Save, RefreshCw, Upload, X, Play, MapPin, Phone, Mail } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Helper function to get full image URL - fixes URLs from old domains
const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) return `${window.location.origin}${match[0]}`;
  }
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CMSTeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { const res = await api.get('/cms/team'); setTeam(res.data); }
    catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const { data } = editModal;
    try {
      if (data.id) { await api.put(`/cms/team/${data.id}`, data); }
      else { await api.post('/cms/team', data); }
      toast.success('Enregistré'); loadData(); setEditModal({ open: false, data: null });
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/cms/team/${deleteModal.id}`); toast.success('Supprimé'); loadData(); setDeleteModal({ open: false, id: null }); }
    catch (error) { toast.error('Erreur'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; 
    if (!file) return;
    
    const formData = new FormData(); 
    formData.append('file', file); 
    formData.append('folder', 'team');
    
    try { 
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }); 
      setEditModal({...editModal, data: {...editModal.data, photo_url: res.data.url}}); 
      toast.success('Photo uploadée'); 
    }
    catch (error) { 
      console.error('Upload error:', error);
      toast.error('Erreur upload'); 
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Équipe</h1><p className="text-slate-500">Gérez les membres de votre équipe</p></div>
        <Button onClick={() => setEditModal({ open: true, data: { is_active: true, skills: [], order: team.length + 1 } })} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Plus className="w-4 h-4 mr-2" />Nouveau membre</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {team.map(member => (
          <Card key={member.id}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${member.color || 'from-cyan-500 to-blue-600'} flex items-center justify-center text-white text-xl font-bold overflow-hidden`}>
                  {member.photo_url ? <img src={getFullImageUrl(member.photo_url)} alt={member.name} className="w-full h-full object-cover" /> : member.name?.charAt(0)}
                </div>
                <div className="flex-1"><h3 className="font-semibold">{member.name}</h3><p className="text-sm text-slate-500">{member.role}</p><p className="text-xs text-slate-400">{member.experience}</p></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1">
                {member.video_url && <Badge className="bg-red-100 text-red-600"><Play className="w-3 h-3 mr-1" />Vidéo</Badge>}
                {member.email && <Badge className="bg-blue-100 text-blue-600"><Mail className="w-3 h-3 mr-1" />{member.show_email !== false ? 'Email' : 'Caché'}</Badge>}
                {(member.phone || member.mobile) && <Badge className="bg-green-100 text-green-600"><Phone className="w-3 h-3 mr-1" />{member.show_phone !== false ? 'Tél.' : 'Caché'}</Badge>}
                {(member.skills || []).map((skill, i) => <Badge key={i} variant="secondary" className="text-xs">{skill}</Badge>)}
              </div>
              <div className="mt-4 flex justify-end gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, data: member })}><Edit className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, id: member.id })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, data: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editModal.data?.id ? 'Modifier' : 'Nouveau'} membre</DialogTitle></DialogHeader>
          {editModal.data && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Nom</Label><Input value={editModal.data.name || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, name: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Poste</Label><Input value={editModal.data.role || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, role: e.target.value}})} /></div>
              </div>
              <div className="space-y-2"><Label>Bio</Label><Textarea value={editModal.data.bio || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, bio: e.target.value}})} /></div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Expérience</Label><Input value={editModal.data.experience || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, experience: e.target.value}})} placeholder="10+ ans" /></div>
                <div className="space-y-2"><Label>Couleur</Label><Input value={editModal.data.color || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, color: e.target.value}})} placeholder="from-cyan-500 to-blue-600" /></div>
              </div>
              <div className="space-y-2"><Label>Compétences (une par ligne)</Label><Textarea value={(editModal.data.skills || []).join('\n')} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, skills: e.target.value.split('\n').filter(s => s.trim())}})} rows={3} /></div>
              <div className="space-y-2"><Label>Photo</Label>
                {editModal.data.photo_url ? (
                  <div className="relative inline-block">
                    <img src={getFullImageUrl(editModal.data.photo_url)} alt="Preview" className="h-20 w-20 rounded-full object-cover" />
                    <button onClick={() => setEditModal({...editModal, data: {...editModal.data, photo_url: null}})} className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-cyan-500 transition-colors">
                      <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                      <p className="text-sm text-slate-500">Cliquer pour uploader</p>
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                  </label>
                )}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>LinkedIn</Label><Input value={editModal.data.linkedin_url || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, linkedin_url: e.target.value}})} /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={editModal.data.email || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, email: e.target.value}})} placeholder="prenom@agence-msc.fr" /></div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Téléphone</Label><Input type="tel" value={editModal.data.phone || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, phone: e.target.value}})} placeholder="04 84 49 27 00" /></div>
                <div className="space-y-2"><Label>Téléphone portable</Label><Input type="tel" value={editModal.data.mobile || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, mobile: e.target.value}})} placeholder="06 XX XX XX XX" /></div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                <Label className="text-base font-semibold text-blue-800">Options de contact</Label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editModal.data.show_email !== false} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, show_email: e.target.checked}})}
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm">Afficher l'email sur le site</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editModal.data.show_phone !== false} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, show_phone: e.target.checked}})}
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm">Afficher le téléphone sur le site</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editModal.data.allow_contact_form === true} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, allow_contact_form: e.target.checked}})}
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm">Activer le formulaire de contact</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editModal.data.show_linkedin !== false} 
                      onChange={(e) => setEditModal({...editModal, data: {...editModal.data, show_linkedin: e.target.checked}})}
                      className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm">Afficher LinkedIn sur le site</span>
                  </label>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Département</Label><Input value={editModal.data.department || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, department: e.target.value}})} placeholder="Direction, Marketing..." /></div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Localisation</Label>
                  <Input value={editModal.data.location || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, location: e.target.value}})} placeholder="France, Paris..." />
                </div>
              </div>
              
              {/* Instructions de contact personnalisées */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4">
                <Label className="text-base font-semibold text-amber-800">📞 Instructions de contact téléphonique</Label>
                <div className="space-y-2">
                  <Input 
                    value={editModal.data.phone_instruction || ''} 
                    onChange={(e) => setEditModal({...editModal, data: {...editModal.data, phone_instruction: e.target.value}})}
                    placeholder="Tapez 1 pour le service Direction"
                  />
                  <p className="text-xs text-amber-600">Ex: "Tapez 1", "Demandez le service Commercial", "Poste 204"</p>
                </div>
              </div>
              
              {/* Photo de couverture */}
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-200 space-y-4">
                <Label className="text-base font-semibold text-violet-800">🖼️ Photo de couverture (arrière-plan de la fiche)</Label>
                {editModal.data.cover_photo_url ? (
                  <div className="relative">
                    <img 
                      src={editModal.data.cover_photo_url.startsWith('http') ? editModal.data.cover_photo_url : `${process.env.REACT_APP_BACKEND_URL}${editModal.data.cover_photo_url}`} 
                      alt="Couverture" 
                      className="w-full h-32 object-cover rounded-lg" 
                    />
                    <button 
                      onClick={() => setEditModal({...editModal, data: {...editModal.data, cover_photo_url: null}})} 
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <div className="border-2 border-dashed border-violet-300 rounded-lg p-4 text-center hover:border-violet-500 transition-colors bg-white">
                      <Upload className="w-6 h-6 mx-auto text-violet-400 mb-1" />
                      <p className="text-sm text-violet-500">Cliquer pour uploader une image de couverture</p>
                      <p className="text-xs text-violet-400 mt-1">Format paysage recommandé (16:9)</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*" 
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('folder', 'team-covers');
                          try {
                            const res = await api.post('/cms/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            setEditModal({...editModal, data: {...editModal.data, cover_photo_url: res.data.url}});
                          } catch (err) {
                            console.error('Upload error:', err);
                          }
                        }
                      }} 
                    />
                  </label>
                )}
              </div>
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Play className="w-5 h-5 text-red-500" />
                  Vidéo de présentation YouTube
                </Label>
                <Input 
                  value={editModal.data.video_url || ''} 
                  onChange={(e) => setEditModal({...editModal, data: {...editModal.data, video_url: e.target.value}})} 
                  placeholder="https://www.youtube.com/embed/VIDEO_ID"
                />
                <p className="text-xs text-slate-500">
                  Format requis : URL embed YouTube (ex: https://www.youtube.com/embed/dQw4w9WgXcQ)
                </p>
                {editModal.data.video_url && editModal.data.video_url.includes('youtube') && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-slate-900">
                    <iframe
                      src={editModal.data.video_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Aperçu vidéo"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>Annuler</Button><Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Save className="w-4 h-4 mr-2" />Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, id: null })}>
        <DialogContent><DialogHeader><DialogTitle>Supprimer ce membre ?</DialogTitle></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, id: null })}>Annuler</Button><Button variant="destructive" onClick={handleDelete}>Supprimer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSTeamPage;
